// =======================
// src/componentes/comuns/PaginacaoLista.js
// =======================

import React, { useMemo } from "react";

import { Box, Button } from "@mui/material";

import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

import {
  botaoNavegacaoPaginacao,
  botaoNumeroPaginacao,
  botaoNumeroPaginacaoAtivo,
  botaoProximaPaginacao,
  containerPaginacao,
  reticenciasPaginacao,
} from "../../estilos/paginacao";

export default function PaginacaoLista({
  paginaAtual,
  totalPaginas,
  onChange,
  ariaLabel = "Paginação da lista",
}) {
  const total = Math.max(1, Number(totalPaginas) || 1);

  const atual = Math.min(Math.max(1, Number(paginaAtual) || 1), total);

  /*
   * Comportamento aprovado:
   *
   * Página 1:
   * 1 2 3 ... 10
   *
   * Página 3:
   * 1 2 3 4 ... 10
   *
   * Meio:
   * 1 ... 4 5 6 ... 10
   *
   * Final:
   * 1 ... 8 9 10
   */
  const paginasVisiveis = useMemo(() => {
    if (total <= 5) {
      return Array.from({ length: total }, (_, index) => index + 1);
    }

    /*
     * Início.
     */
    if (atual <= 2) {
      return [1, 2, 3, "...", total];
    }

    /*
     * Ao selecionar a página 3,
     * a próxima página já aparece.
     */
    if (atual === 3) {
      return [1, 2, 3, 4, "...", total];
    }

    /*
     * Meio.
     */
    if (atual < total - 2) {
      return [1, "...", atual - 1, atual, atual + 1, "...", total];
    }

    /*
     * Entrada no trecho final.
     */
    if (atual === total - 2) {
      return [1, "...", atual - 1, atual, atual + 1, total];
    }

    /*
     * Final.
     */
    return [1, "...", total - 2, total - 1, total];
  }, [atual, total]);

  const alterarPagina = (novaPagina) => {
    const paginaDestino = Math.min(Math.max(1, Number(novaPagina) || 1), total);

    if (paginaDestino === atual) {
      return;
    }

    onChange?.(paginaDestino);
  };

  return (
    <Box component="nav" aria-label={ariaLabel} sx={containerPaginacao}>
      {/* ANTERIOR */}

      <Button
        type="button"
        size="small"
        variant="text"
        startIcon={
          <ChevronLeftRoundedIcon
            sx={{
              fontSize: 19,
            }}
          />
        }
        disabled={atual <= 1}
        onClick={() => alterarPagina(atual - 1)}
        sx={botaoNavegacaoPaginacao}
      >
        Anterior
      </Button>

      {/* NÚMEROS */}

      {paginasVisiveis.map((item, index) => {
        if (item === "...") {
          return (
            <Box
              key={`reticencias-${index}`}
              component="span"
              aria-hidden="true"
              sx={reticenciasPaginacao}
            >
              …
            </Box>
          );
        }

        const selecionada = item === atual;

        return (
          <Button
            key={item}
            type="button"
            size="small"
            variant="text"
            aria-label={
              selecionada
                ? `Página ${item}, página atual`
                : `Ir para a página ${item}`
            }
            aria-current={selecionada ? "page" : undefined}
            onClick={() => alterarPagina(item)}
            sx={selecionada ? botaoNumeroPaginacaoAtivo : botaoNumeroPaginacao}
          >
            {item}
          </Button>
        );
      })}

      {/* PRÓXIMA */}

      <Button
        type="button"
        size="small"
        variant="text"
        endIcon={
          <ChevronRightRoundedIcon
            sx={{
              fontSize: 19,
            }}
          />
        }
        disabled={atual >= total}
        onClick={() => alterarPagina(atual + 1)}
        sx={botaoProximaPaginacao}
      >
        Próxima
      </Button>
    </Box>
  );
}
